type Listener = () => void

/**
 * Per-request opt-out from latency sampling, carried on the axios request
 * config and read by BOTH `recordLatency` call sites in `api-client.ts`
 * (the response interceptor and the error interceptor).
 *
 * Why an opt-OUT and not a denylist: sampling must be the default so a new
 * endpoint is measured without anyone remembering to register it. The flag
 * belongs on the request that knows it is not a latency measurement, not on
 * a list in a file nobody edits.
 *
 * When to set it: the request's duration is dominated by SERVER WORK rather
 * than by the link — today that means the endpoints that render a report PDF
 * synchronously inside the request and therefore raise the axios timeout from
 * the 15s default to 300s. Their duration says nothing about connection
 * quality, and feeding one of them into a 5-sample window with a 3000ms
 * threshold pins the mean above `SLOW_THRESHOLD_MS` for the next samples
 * regardless of how fast the link actually is.
 *
 * Do NOT set it to silence a merely slow endpoint. A slow endpoint on a slow
 * link is exactly what this detector exists to notice.
 */
declare module 'axios' {
  interface AxiosRequestConfig {
    skipLatencySampling?: boolean
  }
}

/**
 * Sliding window of the last N request durations (ms).
 * SLOW_THRESHOLD_MS / FAST_THRESHOLD_MS with a dead zone in between:
 *   > 3000ms avg → slow signal
 *   < 1000ms avg → fast signal
 *   1000–3000ms  → dead zone, does NOT reset consecutive counters
 *
 * REQUIRED_CONSECUTIVE consecutive evaluations above/below threshold
 * are needed before a state transition fires (hysteresis to prevent flip-flop).
 */
const WINDOW_SIZE = 5
const SLOW_THRESHOLD_MS = 3000
const FAST_THRESHOLD_MS = 1000
const REQUIRED_CONSECUTIVE = 3
const MIN_SAMPLES = 3

export class SlowConnectionDetector {
  private latencies: number[] = []
  private consecutiveSlow = 0
  private consecutiveFast = 0
  private slow = false
  private slowListeners = new Set<Listener>()
  private fastListeners = new Set<Listener>()

  recordLatency(durationMs: number): void {
    this.latencies = [...this.latencies, durationMs].slice(-WINDOW_SIZE)

    if (this.latencies.length < MIN_SAMPLES) return

    const avg = this.latencies.reduce((sum, d) => sum + d, 0) / this.latencies.length

    if (avg > SLOW_THRESHOLD_MS) {
      this.consecutiveSlow++
      this.consecutiveFast = 0
      if (!this.slow && this.consecutiveSlow >= REQUIRED_CONSECUTIVE) {
        this.slow = true
        this.slowListeners.forEach(fn => fn())
      }
    } else if (avg < FAST_THRESHOLD_MS) {
      this.consecutiveFast++
      this.consecutiveSlow = 0
      if (this.slow && this.consecutiveFast >= REQUIRED_CONSECUTIVE) {
        this.slow = false
        this.fastListeners.forEach(fn => fn())
      }
    }
    // Dead zone (1000–3000ms): neither counter advances, neither resets.
    // A borderline request does not delay detection.
  }

  isSlow(): boolean {
    return this.slow
  }

  onSlowDetected(listener: Listener): () => void {
    this.slowListeners.add(listener)
    return () => {
      this.slowListeners.delete(listener)
    }
  }

  onFastDetected(listener: Listener): () => void {
    this.fastListeners.add(listener)
    return () => {
      this.fastListeners.delete(listener)
    }
  }

  reset(): void {
    this.latencies = []
    this.consecutiveSlow = 0
    this.consecutiveFast = 0
    this.slow = false
  }
}

export const slowConnectionDetector = new SlowConnectionDetector()
